import { GlobalContext } from "../GlobalContext";
import { MemoryUtils } from "../Utils/MemoryUtils";
import { StringUtils } from "../Utils/StringUtils";
import { Base64js } from "./Base64js";

export class DataConverter {
    public static bufferToBase64(buffer: BufferSource): string {
        return _base64ArrayBuffer(MemoryUtils.bufferSourceToArrayBuffer(buffer));
    }

    public static base64ToArrayBuffer(b64Encoded: string): ArrayBuffer {
        return DataConverter.base64ToArray(b64Encoded).buffer as ArrayBuffer;
    }

    public static base64ToArray(input: string): Uint8Array {
        // new Uint8Array([...atob(input)].map((v) => v.charCodeAt(0)))

        /*
        // mnohem rychlejsi a efektivnejsi verze predesleho reseni
        const binary = atob(input);
        const len = binary.length >>> 0;
        const view = new Uint8Array(len);
        let i: number;
        for (i = 0; i < len; ++i) view[i] = binary.charCodeAt(i);
        return view;
        */

        return _base64ToUint8Array(input);
    }

    public static toDate(value: string): Date | null {
        if (value) {
            const ret = new Date(value);
            if (isNaN(ret.getTime()))
                return null;
            else
                return ret;
        } else {
            return null;
        }
    }

    /**
     * Vrátí datum ve formátu pro podmínky
     * @param value Datum
     * @param withTime Přepínač, zda to vrátit i s časem nebo ne.
     */
    public static toConditionDateString(value: Date, withTime: boolean = false): string | null {
        if (value == null)
            return null;
        const f_pad = (v: number) => (v < 10) ? `0${v}` : v.toString(); // `

        const retString = `${value.getFullYear()}-${f_pad(value.getMonth() + 1)}-${f_pad(value.getDate())}`; // `
        if (!withTime)
            return retString;

        return retString + ` ${f_pad(value.getHours())}:${f_pad(value.getMinutes())}:${f_pad(value.getSeconds())}`; //`
    }

    /**
        * Konvertuje danou hodnotu na boolean, pokud neodpovida hodnote
        * @param string Obsahuje hodnotu pro parsovani
        * @param any Obsahuje defaultni navratovou hodnotu v pripade ze neprojde parsovanim
        */
    public static toBoolean(value: string | null | undefined, defaultReturn: any = false): boolean {
        if (value == null)
            return defaultReturn;
        else if (typeof value === "string") {
            value = value.toLowerCase();
            if (value === "true")
                return true;
            else if (value === "false")
                return false;
            else
                return defaultReturn;
        } else
            return defaultReturn;
    }

    public static toNumber(value: string): number {
        return Number(value);
    }

    /** Konvertuje string na číslo. Pokud je hodnota undefined vrací místo NaN vrací undefined. */
    public static toNumberOrUndefined(value: string | undefined): number | undefined {
        return value ? Number(value) : undefined;
    }

    public static btoaUtf8(str: string): string {
        const encoded = StringUtils.stringToUTF8Bytes(str);
        const b64Encoded = Base64js.fromByteArray(encoded);
        return b64Encoded;
    }

    /**
     * Převede barvu z formátu rgb/rgba do hex
     * @param color - barva komponenty
     * @param background - barva podkladu, na kterém je komponenta umístěna. Je využívána k dopočítání správné barvy v případě existence alfa kanálu
     */
    public static rgbToHex(color: string, background?: string): string | null { // Používáno ColorEditem
        if (color == null || color.startsWith("#")) {
            return color;
        }

        const parsedColor = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*(\d*\.?\d+)?\)*$/);

        if (parsedColor == null) {
            return null;
        }

        let red = Number(parsedColor[1]);
        let blue = Number(parsedColor[2]);
        let green = Number(parsedColor[3]);

        if (background && parsedColor[4]) { // Existuje alfa kanál a je nastavená barva podkladu (např. detailu), provedeme korekci před konverzí na hex
            // https://marcodiiga.github.io/rgba-to-rgb-conversion
            const alpha = Number(parsedColor[4]);

            const parsedBackground = background.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+),?.*\)*$/);

            if (parsedBackground != null) {
                red = Math.round((1 - alpha) * Number(parsedBackground[1]) + alpha * red);
                blue = Math.round((1 - alpha) * Number(parsedBackground[2]) + alpha * blue);
                green = Math.round((1 - alpha) * Number(parsedBackground[3]) + alpha * green);
            }
        }

        return "#" + [red, blue, green].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? "0" + hex : hex;
        }).join("");
    }
}

// https://gist.github.com/jonleighton/958841
// Converts an ArrayBuffer directly to base64, without any intermediate 'convert to string then
// use window.btoa' step. According to my tests, this appears to be a faster approach:
// http://jsperf.com/encoding-xhr-image-data/5

/*
MIT LICENSE
Copyright 2011 Jon Leighton
Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

function _base64ArrayBuffer(arrayBuffer: ArrayBuffer) {
    let base64 = "";
    const encodings = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

    const bytes = new Uint8Array(arrayBuffer);
    const byteLength = bytes.byteLength;
    const byteRemainder = byteLength % 3;
    const mainLength = byteLength - byteRemainder;
    let a: number;
    let b: number;
    let c: number;
    let d: number;
    let chunk: number;

    // Main loop deals with bytes in chunks of 3
    for (let i = 0; i < mainLength; i = i + 3) {
        // Combine the three bytes into a single integer
        chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];

        // Use bitmasks to extract 6-bit segments from the triplet
        a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
        b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
        c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
        d = chunk & 63; // 63       = 2^6 - 1

        // Convert the raw binary segments to the appropriate ASCII encoding
        base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
    }

    // Deal with the remaining bytes and padding
    if (byteRemainder === 1) {
        chunk = bytes[mainLength];
        a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

        // Set the 4 least significant bits to zero
        b = (chunk & 3) << 4; // 3   = 2^2 - 1

        base64 += encodings[a] + encodings[b] + "==";
    } else if (byteRemainder === 2) {
        chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];
        a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
        b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

        // Set the 2 least significant bits to zero
        c = (chunk & 15) << 2; // 15    = 2^4 - 1

        base64 += encodings[a] + encodings[b] + encodings[c] + "=";
    }

    return base64;
}

const ranks = new Uint8Array([
    -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1,
    -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1,
    -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, -1, 62, -1, -1, -1, 63,
    52, 53, 54, 55, 56, 57, 58, 59, 60, 61, -1, -1, -1, 0, -1, -1,
    -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
    15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, -1, -1, -1, -1, -1,
    -1, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
    41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51,
]);

// bezkonkurencne nejrychlejsi reseni ve vetsine prohlizecu
// https://jsperf.com/base64-to-uint8array/18
// v Safari az dvakrat nez ostatni reseni
// neumí dekodovat YW55IGNhcm5hbCBwbGVhcw, ale jen s paddingem YW55IGNhcm5hbCBwbGVhcw==, ale to neumi ani .NET, tak by to melo byt v pohode
// https://en.wikipedia.org/wiki/Base64

function _base64ToUint8Array(input: string): Uint8Array {
    let len = input.length >>> 0;
    const lastEqu = +(input[len - 1] === "="); // konverze bool na cislo
    const scndLastEqu = +(input[len - 2] === "=");
    let i = -1;
    let outptr = -1;
    let save = 0;
    const output = new Uint8Array((len >>> 2) + (len >>> 1) - lastEqu - scndLastEqu);
    len = (len >> 2) - lastEqu;
    while (len--) {
        save = ranks[input.charCodeAt(++i)] << 18;
        save |= ranks[input.charCodeAt(++i)] << 12;
        save |= ranks[input.charCodeAt(++i)] << 6;
        save |= ranks[input.charCodeAt(++i)];
        output[++outptr] = save >>> 16;
        output[++outptr] = save >>> 8;
        output[++outptr] = save;
    }
    if (lastEqu) {
        save = ranks[input.charCodeAt(++i)] << 18;
        save |= ranks[input.charCodeAt(++i)] << 12;
        save |= ranks[input.charCodeAt(++i)] << 6;
        output[++outptr] = save >>> 16;
        if (!scndLastEqu)
            output[++outptr] = save >>> 8;
    }
    return output;
}

GlobalContext.registerType(DataConverter);
